// src/pages/AcercaDePage.jsx

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Fade,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@mui/icons-material/Security';
import VideocamIcon from '@mui/icons-material/Videocam';
import CreditCardIcon from '@mui/icons-material/CreditCard'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import n1coLogo from '../assets/logo-n1co.png';


// Lista de características para renderizar dinámicamente
const features = [
  {
    icon: <HistoryEduIcon />,
    title: "Historias Clínicas Digitales",
    description: "Centralice y gestione de forma segura y eficiente toda la información médica de sus pacientes, desde antecedentes, diagnósticos CIE-11, hasta consultas y exámenes."
  },
  {
    icon: <PeopleIcon />,
    title: "Gestión de Pacientes",
    description: "Mantenga un registro detallado y de fácil acceso de todos sus pacientes, incluyendo datos demográficos, de contacto y observaciones."
  },
  {
    icon: <EventAvailableIcon />,
    title: "Calendario y Citas",
    description: "Organice su agenda con un calendario intuitivo que le permite programar, visualizar y gestionar todas sus citas médicas en un solo lugar."
  },
  {
    icon: <NotificationsActiveIcon />,
    title: "Recordatorios Automáticos",
    description: "Reduzca el ausentismo y mejore la comunicación con sus pacientes mediante recordatorios de citas automáticos y personalizables vía correo electrónico."
  },
  {
    icon: <MonetizationOnIcon />,
    title: "Módulo de Finanzas",
    description: "Lleve un control claro y sencillo de sus ingresos y gastos, genere reportes y mantenga la salud financiera de su práctica profesional."
  },
  {
    icon: <ReceiptLongIcon />,
    title: "Receta Electrónica",
    description: "Genere recetas médicas profesionales en formato PDF, listas para imprimir o enviar digitalmente, mejorando la legibilidad y seguridad."
  },
  {
    icon: <SmartToyIcon />,
    title: "Asistente con IA",
    description: "Consulte información médica, terminología y guías de uso del sistema con nuestro asistente inteligente, diseñado para ser su copiloto digital."
  },
  {
    icon: <VideocamIcon />,
    title: "Telemedicina",
    description: "Realice consultas virtuales con sus pacientes a través de nuestra plataforma integrada de videollamadas, expandiendo el alcance de su práctica médica más allá de las fronteras físicas."
  },
  {
    icon: <CreditCardIcon />,
    title: "Agendamiento automático y Pago en Línea",
    description: "Permita que sus pacientes reserven citas desde su página pública y paguen de forma segura con PayPal. Soporte de múltiples monedas y confirmación automática de citas."
  }
];

const AcercaDePage = () => {
  const theme = useTheme();
  
  // Estilos locales para sobrescribir los del tema
  const paperStyle = {
    p: { xs: 2, sm: 4 },
    borderRadius: '34px', // Bordes más redondeados
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    overflow: 'hidden',
  };

  const cardStyle = {
    height: '100%', 
    display: 'flex', 
    flexDirection: 'column', 
    borderRadius: '22px', // Bordes más redondeados
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'transform 0.3s, box-shadow 0.3s', 
    '&:hover': { 
      transform: 'translateY(-5px)', 
      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
      borderColor: alpha(theme.palette.primary.main, 0.2),
    }
  };

  const avatarStyle = {
    bgcolor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.2)
      : alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    width: 56, 
    height: 56, 
    mr: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={true} timeout={800}>
        <Paper
          elevation={1} // Añadimos una pequeña elevación para asegurar visibilidad
          sx={paperStyle}
        >
          {/* Cabecera */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={avatarStyle}>
              <InfoIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h3" component="h1" fontWeight="bold" color="primary.main">
                Acerca de Medify Healthcare
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Innovación y eficiencia para la práctica médica moderna.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Sección de Introducción - Sin efecto de deslizamiento */}
          <Fade in={true} timeout={1000}>
            <Box sx={{ mb: 5 }}>
              <Typography variant="h5" gutterBottom fontWeight="medium">
                Nuestra Misión
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.7, 
                  textAlign: 'justify',
                  hyphens: 'auto', 
                  letterSpacing: '0.01em' 
                }}
              >
                Medify Healthcare es una solución innovadora de gestión de expedientes clínicos, <strong>orgullosamente de origen hondureño</strong>, diseñada para fortalecer a profesionales de la salud independientes y clínicas pequeñas. Nuestra misión es democratizar el acceso a herramientas tecnológicas de vanguardia, ofreciendo una plataforma robusta, segura y, sobre todo, <strong>de bajo costo</strong>, que optimiza la administración de la práctica médica y mejora la calidad de la atención al paciente.
              </Typography>
            </Box>
          </Fade>

          {/* Sección de Características */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 4 }}>
              ¿Qué puede hacer con MedifyHC?
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in={true} timeout={500 + index * 200}>
                    <Card 
                      elevation={1} // Pequeña elevación para asegurar visibilidad
                      sx={cardStyle}
                    >
                      <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.mode === 'dark' 
                              ? alpha(theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: 60, 
                            height: 60, 
                            margin: '0 auto 16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                          }}
                        >
                          {React.cloneElement(feature.icon, { sx: { fontSize: 30 } })}
                        </Avatar>
                        <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          <Divider sx={{ my: 4 }} />

          {/* Nueva sección de Seguridad en Pagos */}
          <Fade in={true} timeout={1500}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.success.main, 0.2)
                      : alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    width: 48, 
                    height: 48, 
                    mr: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}
                >
                  <SecurityIcon />
                </Avatar>
                <Typography variant="h5" fontWeight="medium">
                  Seguridad en Pagos
                </Typography>
              </Box>
              
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.05rem', 
                      lineHeight: 1.7,
                      mb: 2
                    }}
                  >
                    En MedifyHC, la seguridad de sus transacciones es nuestra prioridad. Nos asociamos con <strong>Pagadito</strong>, una plataforma de pagos certificada y reconocida en Centroamérica, para garantizar que todas sus transacciones sean:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>Seguras:</strong> Protegidas con encriptación de nivel bancario.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>Confiables:</strong> Procesadas por una plataforma certificada internacionalmente.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>Transparentes:</strong> Sin cargos ocultos ni sorpresas en su facturación.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={5}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      p: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: '22px',
                      bgcolor: alpha(theme.palette.background.paper, 0.5)
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                       Comercio certificado por:
                    </Typography>
  
                    {/* Enlace a la verificación de Pagadito con imagen personalizada */}
                    <Box 
                      component="a"
                      href="https://comercios.pagadito.com/validate/index.php?merchant=d5cbfcf778159881dd3549ab4d31c012&_idioma=es"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textDecoration: 'none',
                      transition: 'transform 0.2s',
                      '&:hover': {
                      transform: 'scale(1.05)'
                     }
                   }}
                  >
                   {/* Imagen personalizada del sello de Pagadito */}
                   <Box
                     sx={{
                     width: 180,
                     height: 80,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     border: '1px solid #e0e0e0',
                     borderRadius: '8px',
                     p: 1,
                     bgcolor: '#ffffff',
                     boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                     mb: 1
                   }}
                  >
                   {/* Logo de Pagadito */}
                   <Typography 
                     variant="h6" 
                     component="span" 
                     sx={{ 
                       color: '#2e7d32', 
                       fontWeight: 'bold',
                       display: 'flex',
                       alignItems: 'center'
                     }}
                    >
                     <VerifiedUserIcon sx={{ mr: 1, color: '#2e7d32' }} />
                     Pagadito
                   </Typography>
                 </Box>
                 <Typography variant="caption" color="text.secondary">
                   Haga clic para verificar
                 </Typography>
               </Box>
              </Box>
                </Grid>
                {/* N1CO */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      p: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: '22px',
                      bgcolor: alpha(theme.palette.background.paper, 0.5)
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Comercio Partner de:
                    </Typography>

                    <Box
                      component="a"
                      href="https://n1co.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textDecoration: 'none',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 180,
                          height: 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          p: 1,
                          bgcolor: '#ffffff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          mb: 1
                        }}
                      >
                        <Box
                          component="img"
                          src={n1coLogo}
                          alt="N1CO"
                          sx={{ height: 40, objectFit: 'contain' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Procesador de pagos certificado
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

              </Grid>
            </Box>
          </Fade>

          


          {/* Divider después de la sección de Pagadito */}
          <Divider sx={{ my: 4 }} />

          {/* Nueva sección de Seguridad del Sitio */}
          <Fade in={true} timeout={1800}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.info.main, 0.2)
                      : alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    width: 48, 
                    height: 48, 
                    mr: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}
                >
                  <SecurityIcon />
                </Avatar>
                <Typography variant="h5" fontWeight="medium">
                  Seguridad del Sitio
                </Typography>
              </Box>
              
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.05rem', 
                      lineHeight: 1.7,
                      mb: 2
                    }}
                  >
                    En MedifyHC, nos comprometemos con los más altos estándares de seguridad web. Nuestro sitio ha sido verificado y certificado por múltiples autoridades de seguridad independientes:
                  </Typography>
        
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>Google Safe Browsing:</strong> "No unsafe content found" - Verificado como sitio seguro sin contenido malicioso.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>SSL Labs:</strong> Calificación "A" - Implementación óptima de seguridad SSL/TLS.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="body1">
                      <strong>VirusTotal:</strong> "Clean" - Sin detección de malware o amenazas.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={5}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      p: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: '22px',
                      bgcolor: alpha(theme.palette.background.paper, 0.5)
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Certificaciones de seguridad:
                    </Typography>

                    {/* Certificados de seguridad */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      justifyContent: 'center', 
                      gap: 2 
                    }}>
                      {/* Google Safe Browsing */}
                      <Box 
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          bgcolor: '#ffffff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          width: 120,
                          height: 80,
                          justifyContent: 'center'
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          component="span" 
                          sx={{ 
                            color: '#4285F4', 
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: '0.75rem'
                          }}
                        >
                          Google
                        </Typography>
                        <Typography 
                          variant="subtitle2" 
                          component="span" 
                          sx={{ 
                            color: '#34A853', 
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: '0.75rem'
                          }}
                        >
                          Safe Browsing
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mt: 0.5, 
                          bgcolor: '#F8F9FA', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1 
                        }}>
                          <VerifiedUserIcon sx={{ color: '#34A853', fontSize: '0.875rem', mr: 0.5 }} />
                          <Typography variant="caption" sx={{ color: '#34A853', fontWeight: 'medium' }}>
                            Verificado
                          </Typography>
                        </Box>
                      </Box>

                      {/* SSL Labs */}
                      <Box 
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          bgcolor: '#ffffff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          width: 120,
                          height: 80,
                          justifyContent: 'center'
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          component="span" 
                          sx={{ 
                            color: '#1976D2', 
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: '0.75rem'
                          }}
                        >
                          SSL Labs
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mt: 0.5,
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          bgcolor: '#4CAF50',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          A
                        </Box>
                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                          Rating
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>

          <Divider sx={{ my: 4 }} />

          {/* Conclusión */}
          <Fade in={true} timeout={2000}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                color="primary.dark" 
                fontWeight="medium"
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.8
                }}
              >
                En MedifyHC, creemos que la tecnología de punta no debe ser un lujo, sino una herramienta accesible para todos los profesionales que dedican su vida a cuidar de los demás.
              </Typography>
            </Box>
          </Fade>

        </Paper>
      </Fade>
    </Container>
  );
};

export default AcercaDePage;
